import React, { useState } from 'react';
import { Form, Input, Button, Checkbox, Typography, message, Row, Col } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { authAPI } from '../services/api';
import { sanitizeEmail, sanitizeInput } from '../utils/validation';
 
const { Title, Text } = Typography;
 
interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}
 
const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login } = useAuthStore();
 
  const BrandIcon = () => (
    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
      <img
        src="/logo.png"
        alt="Logo"
        style={{
          width: '180px',
          height: '180px',
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto'
        }}
      />
    </div>
  );
 
  const onFinish = async (values: LoginFormValues) => {
    setLoading(true);
    try {
      // Sanitize inputs
      const sanitizedValues = {
        ...values,
        email: sanitizeEmail(values.email),
        password: sanitizeInput(values.password)
      };
 
      const response = await authAPI.login(sanitizedValues);
      const { user, token } = response.data;
      
      login(user, token);
      message.success('Login successful!');
      
      // Only super admins go to client selection (they can manage multiple clients)
      // Company admins and users go directly to dashboard (they're locked to their company)
      if (user.role === 'super_admin') {
        navigate('/client-selection');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Login error details:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        fullError: error.response
      });
      
      // Get the specific error message from backend
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      
      // Display the backend error message directly
      message.error(errorMessage, 5); // Show for 5 seconds
      
      // Log specific error types for debugging
      if (error.response?.status === 404) {
        console.log('User not found error');
      } else if (error.response?.status === 401) {
        console.log('Authentication error (wrong password or inactive account)');
      } else if (error.response?.status === 403) {
        console.log('Account access forbidden');
      } else if (error.response?.status === 429) {
        console.log('Rate limit exceeded');
      } else if (error.response?.status === 400) {
        console.log('Validation error');
      }
    } finally {
      setLoading(false);
    }
  };
 
  return (
    <div className="auth-page" style={{ overflow: 'hidden' }}>
      <Row style={{ width: '100%', height: '100vh', overflow: 'hidden' }}>
        <Col xs={24} md={12} lg={14} xl={12}>
          <div style={{
            padding: '2rem 2rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100vh',
            maxWidth: '500px',
            margin: '0 auto',
            overflow: 'hidden'
          }}>
            <BrandIcon />
 
            <Title level={2} className="page-title" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
              Welcome back
            </Title>
 
            <Form
              form={form}
              name="login"
              onFinish={onFinish}
              autoComplete="off"
              layout="vertical"
              size="large"
            >
              <Form.Item
                label={<span className="form-item-label">Email</span>}
                name="email"
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'Please enter a valid email!' }
                ]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#47569e' }} />}
                  placeholder="Enter your email"
                />
              </Form.Item>
 
              <Form.Item
                label={<span className="form-item-label">Password</span>}
                name="password"
                rules={[{ required: true, message: 'Please input your password!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#47569e' }} />}
                  placeholder="Enter your password"
                />
              </Form.Item>
 
              <div className="form-footer">
                <Form.Item name="rememberMe" valuePropName="checked" style={{ margin: 0 }}>
                  <Checkbox className="remember-checkbox">Remember me</Checkbox>
                </Form.Item>
                <Link to="/forgot-password" className="forgot-password-link">
                  Forgot Password?
                </Link>
              </div>
 
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  className="login-btn"
                >
                  Log In
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Col>
        
        <Col xs={0} md={12} lg={10} xl={12}>
          <div style={{
            height: '100vh',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="none"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0
              }}
            >
              <source src="https://cdn.jsdelivr.net/gh/NivedKris/cdn@main/output_novoice.mp4" type="video/mp4" />
            </video>
          </div>
        </Col>
      </Row>
    </div>
  );
};
 
export default LoginPage;